import React from "react";
import { Link } from "react-router-dom";
import { WhatsappLogo, ArrowRight, CheckCircle, Phone } from "@phosphor-icons/react";
import Reveal from "../../components/Reveal";
import useSeo from "../../lib/useSeo";

/**
 * Shared SEO landing-page shell.
 *
 * Renders:
 *   • useSeo() with page-specific title, description, keywords, canonical
 *   • Hero — H1 (focus keyword), lede, dual WhatsApp + Book CTA
 *   • Rich body sections with H2 subheads
 *   • Trust strip (Est. 2008, 50,000+ patients, 100% confidential)
 *   • FAQ block
 *   • Closing CTA
 *
 * All copy is passed in via props — narrative, no drug names/doses.
 */
export default function SeoLandingPage({
  seo,
  h1,
  lede,
  heroImageAlt,
  heroImage,
  bullets,
  h2,
  h2Body,
  sections,
  faqs,
  whatsappText = "Hello Yuva Ayurvedic, I'd like to book a consultation.",
}) {
  useSeo(seo);
  const waHref =
    "https://wa.me/919519078666?text=" + encodeURIComponent(whatsappText);

  return (
    <div className="pb-24" data-testid={`seo-page-${(seo.canonical || h1).toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}`}>
      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-7">
            <div className="overline">Prayagraj · Since 2008</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              {h1}
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-foreground/90 max-w-2xl">
              {lede}
            </p>
            {bullets?.length > 0 && (
              <ul className="mt-8 space-y-3">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-base leading-relaxed">
                    <CheckCircle size={20} weight="duotone" className="text-sage mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="seo-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 hover:-translate-y-0.5 transition-all duration-500"
              >
                <WhatsappLogo size={18} weight="fill" /> WhatsApp the clinic
              </a>
              <Link
                to="/book-appointment"
                data-testid="seo-cta-book"
                className="inline-flex items-center gap-3 rounded-full bg-forest text-cream px-7 py-3.5 hover:bg-terracotta transition-all duration-500 hover:-translate-y-0.5"
              >
                Book appointment <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+919519078666"
                className="inline-flex items-center gap-2 rounded-full border border-forest text-forest px-6 py-3.5 hover:bg-forest hover:text-cream transition-all"
              >
                <Phone size={16} weight="duotone" /> Call +91 95190 78666
              </a>
            </div>
          </Reveal>
          {heroImage && (
            <Reveal delay={0.15} className="md:col-span-5">
              <img
                src={heroImage}
                alt={heroImageAlt || h1}
                className="rounded-2xl w-full h-auto object-cover shadow-lg"
                loading="lazy"
                decoding="async"
              />
            </Reveal>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-border/50 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-wrap justify-around gap-6 text-xs uppercase tracking-[0.25em] text-forest">
          <span>Est. 2008</span>
          <span>50,000+ patients</span>
          <span>100% confidential</span>
          <span>No side effects</span>
          <span>English & हिंदी</span>
        </div>
      </section>

      {/* Body sections */}
      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 space-y-14">
          {sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
                {i === 0 ? h2 : s.heading}
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85">
                {(i === 0 && h2Body ? [h2Body, ...s.paragraphs] : s.paragraphs).map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
              {s.checklist && (
                <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {s.checklist.map((c, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm">
                      <CheckCircle size={16} weight="duotone" className="text-sage mt-1 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {faqs?.length > 0 && (
        <section className="py-20 bg-sand/30">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="overline">Frequently asked</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-3">Common questions</h2>
            <div className="mt-10 space-y-5">
              {faqs.map((f, i) => (
                <details key={i} className="bg-ivory border border-border/50 rounded-2xl p-6 group">
                  <summary className="cursor-pointer font-medium text-base flex items-center justify-between">
                    <span>{f.q}</span>
                    <span className="text-terracotta group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-4 text-sm leading-relaxed text-foreground/80">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-forest text-cream rounded-2xl p-10 lg:p-16 text-center">
            <div className="overline text-cream/70">Confidential, respectful, effective</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3">
              Ready to speak with the doctor?
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-cream/85">
              Every consultation is private, unhurried and rooted in classical Ayurveda.
              Your identity and concern stay strictly between you and the physician.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 hover:-translate-y-0.5 transition-all"
              >
                <WhatsappLogo size={18} weight="fill" /> WhatsApp us
              </a>
              <Link
                to="/book-appointment"
                className="inline-flex items-center gap-3 rounded-full bg-cream text-forest px-7 py-3.5 hover:bg-terracotta hover:text-cream transition-all"
              >
                Book a visit <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
